import React, { useState } from 'react';
import PerformanceMetricInputCard from './PerformanceMetricInputCard';
import {
  MEASUREMENT_CALCULATIONS,
  PERFORMANCE_METRIC_STATUSES,
  QUALITY_DOMAINS,
  TIME_BOUNDS
} from '../../../shared/constants/checkpoint-performance-report-metric-enums';
import AddBoxOutlinedIcon from '@mui/icons-material/AddBoxOutlined';
import IndeterminateCheckBoxOutlinedIcon from '@mui/icons-material/IndeterminateCheckBoxOutlined';
import PerformanceMetricPageInfoBox from './PerformanceMetricPageInfoBox';
import { Collapse } from '@mui/material';
import cx from 'classnames';
import SkeletonWrapper from '../../../shared/components/skeleton/SkeletonWrapper';
import DoubledAsterisk from '@shared/components/DoubledAsterisk';
import Asterisk from '@shared/components/Asterisk';

/**
 * @typedef {{ [field: string]: string | number }} PerformanceMetricFieldChange
 */

/**
 * @typedef {object} PerformanceMetricPageEditViewControlsProps
 * @property {Object} [performanceMetric]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChangeStatus]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChangeLowerUpperLimit]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChangeActual]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChange]
 * @property {(field: string) => void} [handleOnClickMetricField]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChangeTimeBounds]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChangeMeasurementCalculation]
 * @property {(fieldChange: PerformanceMetricFieldChange) => void} [handleOnChangeQualityDomain]
 * @property {Object} [selectedMetricProperty]
 * @property {boolean} [showDescriptions]
 * @property {Object} [validationErrors]
 * @property {Object} pageSettings
 * @property {boolean} [loading]
 * @property {boolean} [canEditNonEditableFields]
 */

/**
 * @param {PerformanceMetricPageEditViewControlsProps} props
 */
const PerformanceMetricPageEditViewControls = props => {
  const {
    performanceMetric,
    handleOnChangeStatus,
    handleOnChangeLowerUpperLimit,
    handleOnChangeActual,
    handleOnChange,
    handleOnClickMetricField,
    handleOnChangeTimeBounds,
    handleOnChangeMeasurementCalculation,
    handleOnChangeQualityDomain,
    selectedMetricProperty,
    showDescriptions,
    pageSettings,
    canEditNonEditableFields,
    validationErrors = {},
    loading = false
  } = props;

  const isMetricStatusUpdate =
    !loading && performanceMetric.status === PERFORMANCE_METRIC_STATUSES.Update.Id;
  const isMetricStatusNa =
    !loading && performanceMetric.status === PERFORMANCE_METRIC_STATUSES.NA.Id;
  const isCalculationTotalCount =
    !loading && performanceMetric.measurementCalculation === MEASUREMENT_CALCULATIONS.TotalCount.Id;
  const isTimeBoundAllTime = !loading && performanceMetric.timeBounds === TIME_BOUNDS.AllTime.Id;
  const isQualityDomainNA = !loading && performanceMetric.qualityDomain === QUALITY_DOMAINS.NA.Id;

  const customOnChangeFunctions = {
    status: handleOnChangeStatus,
    upperLimit: handleOnChangeLowerUpperLimit,
    lowerLimit: handleOnChangeLowerUpperLimit,
    timeBounds: handleOnChangeTimeBounds,
    qualityDomain: handleOnChangeQualityDomain,
    measurementCalculation: handleOnChangeMeasurementCalculation,
    actual: handleOnChangeActual
  };

  const fieldIsDisabled = {
    status: pageSettings.viewOnlyMode || performanceMetric.addedInCurrentCheckpoint,
    upperLimit: pageSettings.viewOnlyMode || !isMetricStatusUpdate,
    lowerLimit: pageSettings.viewOnlyMode || !isMetricStatusUpdate,
    actual: pageSettings.viewOnlyMode || !isMetricStatusUpdate,
    notes: pageSettings.viewOnlyMode || isMetricStatusNa
  };

  const fieldPlaceholders = getFieldCalculatedPlaceholders(
    pageSettings.viewOnlyMode,
    loading,
    performanceMetric
  );

  const [showNonEditableFields, setShowNonEditableFields] = useState(false);

  const handleNonEditableMetricClick = () => {
    setShowNonEditableFields(!showNonEditableFields);
  };

  const getFieldRenderCondition = (field, canEditNonEditableFields) => {
    switch (field) {
      case 'measurementName':
        return false;
      case 'measurementUnit':
        return isCalculationTotalCount;
      case 'numerator':
      case 'denominator':
        return !isCalculationTotalCount;
      case 'timeFrame':
        return !isTimeBoundAllTime;
      case 'qualityDomainDefinition':
        return !canEditNonEditableFields || !isQualityDomainNA;
      case 'customQualityDomainDefinition':
        return isQualityDomainNA && canEditNonEditableFields;
      default:
        return true;
    }
  };

  const metricMeasurementName = performanceMetric ? performanceMetric.measurementName : '';

  return (
    <div className='performance-metric-controls'>
      <PerformanceMetricPageInfoBox
        metricPropertyInfo={selectedMetricProperty}
        showDescriptions={showDescriptions}
        validationErrors={validationErrors}
        isViewOnly={pageSettings.viewOnlyMode}
      />
      <div className={cx('edit-view-container', { centered: showDescriptions })}>
        <SkeletonWrapper show={loading} inheritParentSize>
          <h4>
            {pageSettings.firstSectionTitle} - {metricMeasurementName}
          </h4>
        </SkeletonWrapper>

        <div className='input-section' data-testid='metric-fields'>
          {pageSettings.metricProperties
            .filter(x => x.isEditableAfterSaving)
            .map(fields => {
              const customOnChange = customOnChangeFunctions[fields.field];
              const isDisabled = fieldIsDisabled[fields.field] || fields.disabled;
              const placeholder = fieldPlaceholders[fields.field] || fields.placeholder;

              return (
                <PerformanceMetricInputCard
                  {...fields}
                  key={`card-${fields.field}`}
                  errors={validationErrors}
                  onChange={customOnChange || handleOnChange}
                  handleOnClickMetricField={handleOnClickMetricField}
                  viewOnlyMode={pageSettings.viewOnlyMode}
                  value={!loading ? performanceMetric[fields.field] : ''}
                  disabled={isDisabled}
                  placeholder={placeholder}
                  loading={loading}
                />
              );
            })}
        </div>
        <div
          onClick={handleNonEditableMetricClick}
          className='non-editable-fields-settings'
          data-testid='additional-metric-fields-toggle'>
          <SkeletonWrapper inheritParentSize={true} show={loading}>
            <h4>
              {pageSettings.secondSectionTitle} - {metricMeasurementName}
              {showNonEditableFields ? (
                <IndeterminateCheckBoxOutlinedIcon />
              ) : (
                <AddBoxOutlinedIcon />
              )}
            </h4>
          </SkeletonWrapper>
        </div>
        <Collapse in={showNonEditableFields}>
          <div className='input-section' data-testid='additional-metric-fields'>
            {pageSettings.metricProperties
              .filter(x => !x.isEditableAfterSaving)
              .map(fields => {
                const fieldVisibility = getFieldRenderCondition(
                  fields.field,
                  canEditNonEditableFields
                );
                const customOnChange = customOnChangeFunctions[fields.field];
                if (fields.field === 'qualityDomainDefinition' && fieldVisibility) {
                  return (
                    <PerformanceMetricInputCard
                      {...fields}
                      key={`card-${fields.field}`}
                      errors={validationErrors}
                      handleOnClickMetricField={handleOnClickMetricField}
                      viewOnlyMode={pageSettings.viewOnlyMode}
                      value={
                        !loading
                          ? isQualityDomainNA
                            ? performanceMetric.customQualityDomainDefinition
                            : Object.values(QUALITY_DOMAINS).find(
                                x => x.Id === performanceMetric.qualityDomain
                              ).Description
                          : ''
                      }
                      disabled={true}
                      loading={loading}
                    />
                  );
                }

                return (
                  <Collapse key={`card-${fields.field}`} in={fieldVisibility} unmountOnExit>
                    <PerformanceMetricInputCard
                      {...fields}
                      key={`card-${fields.field}`}
                      errors={validationErrors}
                      handleOnClickMetricField={handleOnClickMetricField}
                      onChange={customOnChange || handleOnChange}
                      viewOnlyMode={pageSettings.viewOnlyMode}
                      value={!loading ? performanceMetric[fields.field] : ''}
                      disabled={!canEditNonEditableFields}
                      loading={loading}
                      titleAsterisk={
                        fields.addDoubleTitleAsterisk ? <DoubledAsterisk /> : <Asterisk />
                      }
                    />
                  </Collapse>
                );
              })}
          </div>
        </Collapse>
      </div>
    </div>
  );
};

const getFieldCalculatedPlaceholders = (isViewOnly, isLoading, performanceMetric) => {
  if (isViewOnly) {
    return {
      upperLimit: 'Upper Limit',
      lowerLimit: 'Lower Limit',
      actual: 'Actual Value',
      notes: 'Notes'
    };
  }

  const referenceDatapoint = performanceMetric.referenceCheckpointDatapoint || {};

  return {
    actual: isLoading || !referenceDatapoint.actual ? '' : String(referenceDatapoint.actual),
    notes: isLoading || !referenceDatapoint.notes ? '' : referenceDatapoint.notes
  };
};

export default PerformanceMetricPageEditViewControls;
